import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { defineMessage, useIntl } from 'react-intl';
import * as R from 'ramda';
import {
  ImportFile,
  InventoryTableParams,
  ProductType,
} from 'utils/apiClient/services/inventory/types';
import { getCanUseGiftcardsV2 } from 'Account/selectors';
import { handleErrorNotification } from 'Common/utils';
import { getLanguage } from 'Common/selectors';
import { ProductsType } from 'utils/apiClient/services/inventory';
import { publishUIMessage } from 'Common/notifications';
import { useApiClient } from '../../utils/apiClient';

/* eslint-disable import/prefer-default-export */
export const useProductDetails = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const id: number | undefined = queryClient.getQueryData([
    'inventory',
    'hover-product',
  ]);
  return useQuery({
    queryKey: ['inventory', 'product', id],
    queryFn: () => apiClient.Inventory.fetchProductDetails(id as number),
    refetchOnWindowFocus: false,
    enabled: id !== undefined,
    staleTime: 1500,
    retryDelay: 1500,
  });
};

export const useProductInfo = (
  id: number | undefined,
  itemType: ProductType,
  isEnabled: boolean
) => {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [
      'inventory',
      itemType === ProductType.Product ? 'product-data' : 'giftcard-data',
      id,
    ],
    queryFn: () => {
      if (itemType === ProductType.Product) {
        return apiClient.Inventory.fetchProductDetails(id as number);
      }
      return apiClient.Inventory.fetchGiftCardDetails(id as number);
    },
    staleTime: 10000,
    refetchOnWindowFocus: false,
    enabled: !Number.isNaN(id as number) && id !== undefined && isEnabled,
  });
};

export const useDeleteProduct = () => {
  const apiClient = useApiClient();
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: apiClient.Inventory.deleteProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'products'],
      });
      publishUIMessage(
        'success',
        defineMessage({
          defaultMessage: 'Product deleted successfully',
        })
      );
    },
    onError: (error) => {
      handleErrorNotification({
        error,
        lang: intl.locale.toLowerCase(),
      });
    },
  });
};

export const useDeleteBulkItems = (itemType: ProductType) => {
  const apiClient = useApiClient();
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: apiClient.Inventory.deleteProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          'inventory',
          itemType === ProductType.Giftcard ? 'giftcards' : 'products',
        ],
      });
      publishUIMessage(
        'success',
        defineMessage({
          defaultMessage: 'Items deleted successfully',
        })
      );
    },
    onError: (error) => {
      handleErrorNotification({
        error,
        lang: intl.locale.toLowerCase(),
      });
    },
  });
};

export const useProducts = (tableParams: InventoryTableParams) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const keyParams = {
    ...R.omit(['selectedItemsIdsByTable'], tableParams),
    ...(tableParams.sorter !== undefined
      ? {
          sorter: {
            columnKey: tableParams.sorter.columnKey,
            order: tableParams.sorter.order,
          },
        }
      : {}),
  };

  const apiHandler = async (params: any) => {
    try {
      const isGCV2 = getCanUseGiftcardsV2();
      const pdtList =
        queryClient.getQueryData([
          'inventory',
          params.filters?.type === 'product' ? 'products' : 'giftcards',
          params,
        ]) ?? [];

      if (params.filters?.type === 'product') {
        const shouldFetchVariations =
          ((pdtList as Array<any>).length === 0 &&
            tableParams.customFilters?.ean?.contains?.length > 0) ||
          tableParams.customFilters?.product_code?.contains?.length > 0;

        let parents: any = [];
        let children: any = [];

        if (shouldFetchVariations) {
          const { results: childrenList, count }: any =
            await apiClient.Inventory.fetchProducts({
              ...R.mergeDeepRight(tableParams, {
                tableParams: {
                  ...(isGCV2 && { gcv2: true }),
                },
              }),
            });

          if (childrenList.length) {
            const idList = childrenList
              .filter((child: any) => child.parent)
              .map((child: any) => child.parent)
              .join(',');

            const { results }: any = await apiClient.Inventory.fetchProducts({
              ...R.mergeDeepRight(tableParams, {
                tableParams: {
                  skip_customFilters: true,
                  filters: {
                    id__in: idList,
                  },
                },
              }),
            });

            parents = results;
          }

          const allProducts = [...parents, ...childrenList];

          const uniqueProducts = R.uniqBy((product) => product.id, allProducts);

          return {
            results: uniqueProducts,
            count,
          };
        }

        const { results: parentsList, count }: any =
          await apiClient.Inventory.fetchProducts({
            ...R.mergeDeepRight(tableParams, {
              // first request should get no variations
              filters: { parent__isnull: true },
              ...(isGCV2 && { gcv2: true }),
            }),
          });

        if (parentsList.length) {
          const parentIds = parentsList
            .map((parent: any) => parent.id)
            .join(',');
          const { results }: any = await apiClient.Inventory.fetchProducts({
            ...R.mergeDeepRight(tableParams, {
              filters: { parent: parentIds },
            }),
          });
          children = results;
        }

        const products = [...parentsList, ...children];
        return {
          results: products,
          count,
        };
      }
      // fetch giftcards
      const { results, count } = await apiClient.Inventory.fetchProducts(
        params
      );

      return {
        results,
        count,
      };
    } catch (error) {
      const lang = getLanguage();
      handleErrorNotification({
        error,
        lang,
      });
      throw error;
    }
  };
  return useQuery({
    queryKey: [
      'inventory',
      tableParams.filters?.type === 'product' ? 'products' : 'giftcards',
      keyParams,
    ],
    queryFn: () => apiHandler(keyParams),
    refetchOnWindowFocus: false,
    retryDelay: 10000,
    staleTime: 10000,
    select: (products: ProductsType) => ({
      results: products.results,
      count: products.count,
    }),
  });
};
export const useUploadImage = () => {
  const apiClient = useApiClient();
  const intl = useIntl();
  return useMutation({
    mutationFn: apiClient.Inventory.manageImage,
    onError: (error) => {
      handleErrorNotification({
        error,
        lang: intl.locale.toLowerCase(),
      });
    },
  });
};
export const useSaveProduct = () => {
  const apiClient = useApiClient();
  const intl = useIntl();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: apiClient.Inventory.saveProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'products'],
        exact: false,
        stale: true,
        refetchType: 'active',
        type: 'active',
      });
      publishUIMessage(
        'success',
        defineMessage({
          defaultMessage: 'Product saved successfully',
        })
      );
    },
    onError: (error) => {
      handleErrorNotification({
        error,
        lang: intl.locale.toLowerCase(),
      });
    },
  });
};

export const useGenerateProductsTemplate = (lang: string) => {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: ['products', 'template'],
    queryFn: () => apiClient.Inventory.generateProductsTemplate(lang),
    enabled: false,
  });
};

export const useUploadProductsTemplate = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userToken,
      import_file,
    }: {
      userToken: string;
      import_file: ImportFile;
    }) => apiClient.Inventory.uploadProductsImport(userToken, import_file),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['products', 'imports'],
      });
    },
  });
};

export const useProductImports = (condtion: boolean) => {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: ['products', 'imports'],
    queryFn: apiClient.Inventory.getProductsImports,
    enabled: condtion,
  });
};

export const useProductAttributes = (currentLanguage: string) => {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: ['products', 'attributes'],
    queryFn: () => apiClient.Inventory.fetchAttributes(currentLanguage),
    enabled: false,
  });
};

export const useAdjustStock = () => {
  const apiClient = useApiClient();
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, values }: { id: number; values: any }) =>
      apiClient.Inventory.adjustStock(id, values),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'products'],
      });
      publishUIMessage(
        'success',
        defineMessage({
          defaultMessage: 'Stock successfully updated',
        })
      );
    },
    onError: (error: any) => {
      handleErrorNotification({
        error,
        lang: intl.locale.toLowerCase(),
      });
    },
  });
};

export const usePushToShop = () => {
  const apiClient = useApiClient();
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, action }: { id: number; action: any }) =>
      apiClient.Inventory.pushToShop(id, action),
    onSuccess: (_, context) => {
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'products'],
      });
      if (context.action === 'publish') {
        publishUIMessage(
          'success',
          defineMessage({
            defaultMessage: 'Product published successfully',
          })
        );
      } else {
        publishUIMessage(
          'success',
          defineMessage({
            defaultMessage: 'Product removed from online store',
          })
        );
      }
    },
    onError: (error: any) => {
      handleErrorNotification({
        error,
        lang: intl.locale.toLowerCase(),
      });
    },
  });
};
